import { useEffect, useState } from 'react';
import { useOAuth2 } from '@tasoskakour/react-use-oauth2';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { ErrorCodes, Routes } from '@/_shared';
import { isPopupBlocked } from '@/_shared/helpers';
import { useNotification } from '@/_shared/hooks';
import { ActionTypes, AuthResponse, getAuthSettingsCall, useLogin } from '../state';

export const LoginSSOScreen = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notify } = useNotification();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showAlertPopupBlocked, setShowAlertPopupBlocked] = useState(false);
  const { isUserLogged } = useLogin();
  const { loginData, tokenData, idpProvider } = getAuthSettingsCall.hook();

  const redirectUri = `${document.location.origin}${Routes.OAuthPopup}`;
  const { getAuth } = useOAuth2<AuthResponse>({
    ...loginData,
    responseType: 'code',
    extraQueryParameters: {
      kc_idp_hint: idpProvider,
    },
    redirectUri,
    exchangeCodeForTokenQueryFn: async callbackParameters => {
      const { headers, ...tokenParams } = tokenData;
      const body = {
        ...callbackParameters,
        ...tokenParams,
        redirect_uri: redirectUri,
      };

      try {
        const response = await fetch(tokenData.tokenUrl, {
          method: 'POST',
          body: new URLSearchParams(body),
          headers: {
            ...headers,
            Origin: `${document.location.origin}`,
          },
        });
        if (!response.ok) {
          throw new Error();
        }

        const token = await response.json();
        sessionStorage.setItem('accessToken', token.access_token);
        return token;
      } catch {
        throw new Error('Failed');
      }
    },
    // onSuccess has an input parameter which is payload
    onSuccess: (user: AuthResponse) => {
      dispatch({ type: ActionTypes.SET_LOGGED_USER, payload: user });
    },
    // onError has an input parameter which is error
    onError: () => {
      notify(t('errors.genericApiError'), 'error');
    },
  });

  useEffect(() => {
    getAuth();

    if (isPopupBlocked()) {
      setShowAlertPopupBlocked(true);
      throw new Error(ErrorCodes.POPUP_BLOCKED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserLogged) {
      navigate(Routes.Homepage);
    }
  }, [isUserLogged, navigate]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return (
    <Box
      bg='primary/background'
      height='100vh'
      width='100vw'
      flexDirection='column'
      display='flex'
      alignItems='center'
    />
  );
};
